const SVG_NS = 'http://www.w3.org/2000/svg'
const INKSCAPE_NS = 'http://www.inkscape.org/namespaces/inkscape'
const SODIPODI_NS = 'http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd'
const GRAPHICS = new Set('g a image path text rect circle ellipse line polyline polygon use'.split(' '))
const CONTAINERS = new Set(['g', 'a'])
const INHERITED = ('color fill fill-opacity fill-rule stroke stroke-opacity stroke-width ' +
  'stroke-dasharray stroke-dashoffset stroke-linecap stroke-linejoin stroke-miterlimit ' +
  'font-family font-size font-style font-weight font-stretch font-variant letter-spacing word-spacing ' +
  'text-anchor text-rendering direction writing-mode dominant-baseline visibility ' +
  'marker-start marker-mid marker-end paint-order').split(' ')

const graphical = element => element?.namespaceURI === SVG_NS && GRAPHICS.has(element.localName)
const computed = element => element.ownerDocument.defaultView.getComputedStyle(element)
const fail = (code, message) => { throw Object.assign(new Error(message), { code }) }

/** Direct paintable children, frontmost first. Nested SVG viewports are not editable tree nodes. */
export const getTreeChildren = parent => Array.from(parent?.children || []).filter(graphical).reverse()

/** Labels are plain text; never borrow a descendant's title. */
export const getNodeLabel = element => {
  const title = Array.from(element.children || []).find(child => child.localName === 'title')
  return title?.textContent?.trim() || element.getAttributeNS(INKSCAPE_NS, 'label') || element.id || element.localName
}

/** Returns the containing registered layer's SVG group, not its name or Layer wrapper. */
export const getNodeLayer = (svgCanvas, element) => {
  const root = svgCanvas.getSvgContent()
  if (!element || element === root || !root.contains(element)) return null
  let node = element
  while (node.parentNode !== root) node = node.parentNode
  const drawing = svgCanvas.getCurrentDrawing()
  for (let i = 0; i < drawing.getNumLayers(); i++) {
    if (drawing.getLayerByName(drawing.getLayerName(i)) === node) return node
  }
  return null
}

// Pointer events describe temporary editing state, not persistent locks.
export const isTreeNodeLocked = (element, root) => {
  for (let node = element; node?.nodeType === 1; node = node.parentElement) {
    if (node.getAttributeNS(SODIPODI_NS, 'insensitive') === 'true') return true
    if (node === root) break
  }
  return false
}

function validNode (canvas, node) {
  if (!graphical(node)) return false
  const layer = getNodeLayer(canvas, node)
  if (!layer) return false
  if (node === layer) return true
  for (let parent = node.parentNode; parent !== layer; parent = parent.parentNode) {
    if (!CONTAINERS.has(parent.localName) || parent.namespaceURI !== SVG_NS) return false
  }
  return true
}

function sources (canvas, elements) {
  const unique = [...new Set(Array.from(elements || []).filter(Boolean))]
  for (const node of unique) {
    if (!validNode(canvas, node)) fail('INVALID_SOURCE', 'The object is no longer in an editable layer.')
    if (getNodeLayer(canvas, node) === node) fail('ROOT_LAYER', 'Use the layer commands to move or delete a root layer.')
    if (isTreeNodeLocked(node, canvas.getSvgContent())) fail('LOCKED_SOURCE', 'Unlock the object or its parent before editing it.')
  }
  return unique.filter(node => !unique.some(other => other !== node && other.contains(node)))
    .sort((a, b) => a.compareDocumentPosition(b) & 4 ? -1 : 1)
}

function visibleDestination (node, root) {
  if (isTreeNodeLocked(node, root)) fail('LOCKED_DESTINATION', 'The destination is locked.')
  for (let parent = node; parent; parent = parent.parentElement) {
    const style = computed(parent)
    if (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility) || Number(style.opacity) === 0) {
      fail('HIDDEN_DESTINATION', 'The destination is hidden.')
    }
    if (parent === root) break
  }
}

function ancestors (node) {
  const result = []
  for (; node; node = node.parentElement) result.push(node)
  return result
}

function checkEffects (source, destination) {
  const from = ancestors(source.parentElement)
  const to = ancestors(destination)
  for (const node of [...from.filter(node => !to.includes(node)), ...to.filter(node => !from.includes(node))]) {
    const style = computed(node)
    const effects = ['filter', 'clip-path', 'mask', 'mask-image', 'mix-blend-mode', 'isolation']
    if ((style.opacity && Number(style.opacity) !== 1) || effects.some(property => {
      const value = style.getPropertyValue(property)
      return value && !['none', 'normal', 'auto'].includes(value)
    })) fail('UNSUPPORTED_EFFECT', 'Moving across opacity, filters, clipping, masks or blending would change the appearance.')
  }
}

const matrixValues = matrix => matrix && ['a', 'b', 'c', 'd', 'e', 'f'].map(key => matrix[key])
const finiteMatrix = matrix => matrixValues(matrix)?.every(Number.isFinite)

function prepareMoves (nodes, destination) {
  let inverse
  return nodes.map(node => {
    if (node.parentNode === destination) return { node }
    checkEffects(node, destination)
    if (node.style.getPropertyValue('transform')) fail('UNSUPPORTED_TRANSFORM', 'An inline CSS transform cannot be transferred as an SVG transform.')
    if (!inverse) {
      const matrix = destination.getCTM()
      if (!finiteMatrix(matrix) || matrix.a * matrix.d - matrix.b * matrix.c === 0) {
        fail('NON_INVERTIBLE_DESTINATION', 'The destination transform cannot be inverted.')
      }
      inverse = matrix.inverse()
    }
    const matrix = node.getCTM()
    if (!finiteMatrix(matrix)) fail('INVALID_TRANSFORM', 'The object transform is unavailable.')
    const result = inverse.multiply(matrix)
    if (!finiteMatrix(result)) fail('NON_INVERTIBLE_DESTINATION', 'The destination transform cannot be inverted.')
    const styles = [node, ...node.querySelectorAll('*')].filter(element => element.namespaceURI === SVG_NS).map(element => {
      const style = computed(element)
      return { element, values: INHERITED.map(property => style.getPropertyValue(property)) }
    })
    return { node, transform: `matrix(${matrixValues(result).join(' ')})`, styles }
  })
}

// Layer identification resets pointer styles; preserve authored values around history.
function keepLayerStyles (canvas, action, activeName) {
  const root = canvas.getSvgContent()
  const styles = [root, ...root.querySelectorAll('*')].map(node => [node, node.getAttribute('style')])
  const drawing = canvas.getCurrentDrawing()
  try {
    return action()
  } finally {
    if (activeName && drawing.getLayerByName(activeName)) drawing.setCurrentLayer(activeName)
    for (const [node, style] of styles) {
      if (style === null) node.removeAttribute('style')
      else node.setAttribute('style', style)
    }
    for (let i = 0; i < drawing.getNumLayers(); i++) {
      const layer = drawing.getLayerByName(drawing.getLayerName(i))
      layer.style.pointerEvents = layer === drawing.getCurrentLayer() && !isTreeNodeLocked(layer, root) ? 'all' : 'none'
    }
  }
}

function layerInsertion (canvas, layer, name, previousName) {
  const command = new canvas.history.InsertElementCommand(layer, 'Create Layer')
  const drawing = canvas.getCurrentDrawing()
  for (const method of ['apply', 'unapply']) {
    const original = command[method].bind(command)
    command[method] = handler => keepLayerStyles(canvas, () => {
      original(handler)
      // Also maintain Drawing when the history is executed without its UI handler.
      if ((drawing.getLayerByName(name) === layer) !== (layer.parentNode === canvas.getSvgContent())) drawing.identifyLayers()
    }, method === 'apply' ? name : previousName)
  }
  return command
}

function changeCommand (canvas, element, oldValues) {
  // Including transform prevents ChangeElementCommand from recentering rotations.
  const command = new canvas.history.ChangeElementCommand(element, oldValues, 'Move Object')
  // The text-coordinate handler writes NaN into tspans for non-coordinate changes.
  if (element.localName === 'text') command.type = () => 'TreeTextStyleCommand'
  return command
}

function restoreSelection (canvas, selection) {
  canvas.selectOnly(selection.filter(node => node && canvas.getSvgContent().contains(node)), true)
}

function contextSafeBatch (canvas, text) {
  const batch = new canvas.history.BatchCommand(text)
  for (const method of ['apply', 'unapply']) {
    const original = batch[method].bind(batch)
    batch[method] = handler => {
      canvas.leaveContext()
      original(handler)
    }
  }
  return batch
}

function outsideContext (canvas, operation) {
  const context = canvas.getCurrentGroup()
  const selection = [...canvas.getSelectedElements()]
  canvas.leaveContext()
  const restore = () => {
    if (context && canvas.getSvgContent().contains(context)) canvas.setContext(context)
    restoreSelection(canvas, selection)
  }
  try {
    const result = operation()
    if (!result.length) restore()
    return result
  } catch (error) {
    restore()
    throw error
  }
}

// A null anchor means frontmost; creating a layer also requires a null destination.
export function moveTreeElements (svgCanvas, elements, destination, before = null, { newLayerName } = {}) {
  return outsideContext(svgCanvas, () => {
    const nodes = sources(svgCanvas, elements)
    if (!nodes.length) return []
    const root = svgCanvas.getSvgContent()
    const drawing = svgCanvas.getCurrentDrawing()
    const creating = newLayerName !== undefined
    let name
    if (creating) {
      if (typeof newLayerName !== 'string' || !newLayerName.trim()) fail('INVALID_LAYER_NAME', 'Enter a non-empty layer name.')
      name = newLayerName.trim()
      if (drawing.hasLayer(name)) fail('DUPLICATE_LAYER_NAME', 'A layer with this name already exists.')
      if (destination !== null || before !== null) fail('INVALID_DESTINATION', 'A new layer cannot have an existing destination or insertion anchor.')
      // A new layer shares the root CTM, so preflight needs no document mutation.
      destination = root
    } else if (!validNode(svgCanvas, destination) || !CONTAINERS.has(destination.localName)) {
      fail('INVALID_DESTINATION', 'Choose an editable layer or group.')
    }
    if (nodes.some(node => node === destination || node.contains(destination))) fail('CYCLE', 'An object cannot be moved inside itself.')
    if (before !== null && before.parentNode !== destination) fail('INVALID_BEFORE', 'The insertion anchor is not a child of the destination.')
    visibleDestination(destination, root)
    // SVG links cannot contain other links.
    if (ancestors(destination).some(node => node.localName === 'a') && nodes.some(node => node.localName === 'a' || node.querySelector('a'))) {
      fail('INVALID_DESTINATION', 'SVG links cannot be nested.')
    }
    while (before && nodes.includes(before)) before = before.nextSibling
    if (!creating && nodes.every(node => node.parentNode === destination)) {
      const oldOrder = Array.from(destination.childNodes)
      const order = oldOrder.filter(node => !nodes.includes(node))
      order.splice(before ? order.indexOf(before) : order.length, 0, ...nodes)
      if (order.every((node, index) => node === oldOrder[index])) return []
    }
    const plans = prepareMoves(nodes, destination)
    const { MoveElementCommand } = svgCanvas.history
    const batch = contextSafeBatch(svgCanvas, creating ? 'Move Objects to New Layer' : 'Move Objects')
    const previousName = drawing.getCurrentLayerName()
    try {
      if (creating) {
        destination = keepLayerStyles(svgCanvas, () => drawing.createLayer(name), name)
        batch.addSubCommand(layerInsertion(svgCanvas, destination, name, previousName))
      }
      for (const { node, transform, styles } of plans) {
        const parent = node.parentNode
        const next = node.nextSibling
        destination.insertBefore(node, before)
        batch.addSubCommand(new MoveElementCommand(node, next, parent))
        if (transform) {
          const oldValues = { transform: node.getAttribute('transform') }
          node.setAttribute('transform', transform)
          batch.addSubCommand(changeCommand(svgCanvas, node, oldValues))
          // Restore parents first to avoid freezing relative values on children.
          for (const { element, values } of styles) {
            const oldStyle = element.getAttribute('style')
            const style = computed(element)
            INHERITED.forEach((property, index) => {
              if (values[index] && style.getPropertyValue(property) !== values[index]) {
                element.style.setProperty(property, values[index], element.style.getPropertyPriority(property))
              }
            })
            if (element.getAttribute('style') !== oldStyle) {
              batch.addSubCommand(changeCommand(svgCanvas, element, { style: oldStyle, transform: element.getAttribute('transform') }))
            }
          }
        }
      }
    } catch (error) {
      batch.unapply()
      throw error
    }
    const layer = getNodeLayer(svgCanvas, destination)
    if (layer) {
      const index = Array.from({ length: drawing.getNumLayers() }, (_, i) => i)
        .find(i => drawing.getLayerByName(drawing.getLayerName(i)) === layer)
      keepLayerStyles(svgCanvas, () => drawing.setCurrentLayer(drawing.getLayerName(index)))
    }
    svgCanvas.addCommandToHistory(batch)
    svgCanvas.call('changed', creating ? [destination, ...nodes] : nodes)
    restoreSelection(svgCanvas, nodes)
    return nodes
  })
}

/** Delete object roots as one undoable operation. Root layers use the layer API. */
export function deleteTreeElements (svgCanvas, elements) {
  return outsideContext(svgCanvas, () => {
    const nodes = sources(svgCanvas, elements)
    if (!nodes.length) return []
    const { RemoveElementCommand } = svgCanvas.history
    const batch = contextSafeBatch(svgCanvas, 'Delete Objects')
    for (const node of nodes) {
      const command = new RemoveElementCommand(node, node.nextSibling, node.parentNode)
      if (!command.nextSibling) {
        // Null means append, not the lost-reference error reported by the core.
        command.unapply = handler => svgCanvas.history.Command.prototype.unapply.call(command, handler, () => {
          command.parent.append(command.elem)
        })
      }
      node.remove()
      batch.addSubCommand(command)
    }
    svgCanvas.addCommandToHistory(batch)
    svgCanvas.clearSelection()
    svgCanvas.call('changed', nodes)
    return nodes
  })
}
